import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import {
  fetchPwaAuthUser,
  fetchAgentAuthUser,
  logoutAgentAccount,
  logoutPwaAccount,
  logoutTelegram,
  type AgentAuthUser,
  type PwaAuthUser,
  type TelegramUser
} from '../../utils/telegramAuth';

const HeaderContainer = styled.header`
  position: sticky;
  top: 0;
  left: 0;
  right: 0;
  height: 60px;
  background: #000000;
  border-bottom: 1px solid #333333;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 16px;
  z-index: 999;
  
  /* Desktop PWA constraint */
  @media (min-width: 768px) {
    max-width: 414px;
    margin: 0 auto;
  }
`;

const LogoSection = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
`;

const Logo = styled.img`
  height: 28px;
  width: auto;
`;

const StatusButton = styled.button<{ $isConnected: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  border-radius: 12px;
  background: ${props => props.$isConnected 
    ? 'rgba(16, 185, 129, 0.15)' 
    : 'linear-gradient(135deg, #10b981 0%, #059669 100%)'};
  color: ${props => props.$isConnected ? '#10b981' : '#ffffff'};
  border: ${props => props.$isConnected 
    ? '1px solid rgba(16, 185, 129, 0.3)' 
    : 'none'};
  cursor: pointer;
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-weight: 600;
  font-size: 14px;
  transition: all 0.2s ease;
  white-space: nowrap;
  
  &:hover {
    background: ${props => props.$isConnected 
      ? 'rgba(16, 185, 129, 0.2)' 
      : 'linear-gradient(135deg, #34d399 0%, #10b981 100%)'};
    border-color: rgba(16, 185, 129, 0.5);
    transform: translateY(-1px);
  }
  
  &:active {
    transform: translateY(0);
  }
`;

const StatusIcon = styled.i`
  font-size: 16px;
`;

const StatusText = styled.span`
  font-size: 12px;
  font-family: 'Plus Jakarta Sans', sans-serif;
  max-width: 100px;
  overflow: hidden;
  text-overflow: ellipsis;
  display: flex;
  align-items: center;
  gap: 4px;
  
  @media (max-width: 480px) {
    max-width: 80px;
    font-size: 11px;
  }
`;

const LogoutButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 10px;
  border-radius: 10px;
  border: 1px solid rgba(255, 255, 255, 0.12);
  background: rgba(255, 255, 255, 0.04);
  color: rgba(255, 255, 255, 0.75);
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    color: #ffffff;
    border-color: rgba(255, 255, 255, 0.25);
  }
`;

const SentryPWAHeader: React.FC<{ mode?: 'pwa' | 'agent' }> = ({ mode = 'pwa' }) => {
  const navigate = useNavigate();
  const [telegramUser, setTelegramUser] = useState<PwaAuthUser | TelegramUser | null>(null);
  const [agentUser, setAgentUser] = useState<AgentAuthUser | null>(null);

  useEffect(() => {
    let cancelled = false;
    const loadUser = async () => {
      if (mode === 'agent') {
        const user = await fetchAgentAuthUser();
        if (!cancelled) {
          setAgentUser(user);
        }
        return;
      }

      const user = await fetchPwaAuthUser();
      if (!cancelled) {
        setTelegramUser(user);
      }
    };
    loadUser();
    return () => {
      cancelled = true;
    };
  }, [mode]);

  const handleStatusClick = () => {
    if (mode === 'agent') {
      if (agentUser) {
        navigate('/sentry-agent/wallet');
      } else {
        navigate('/sentry-pwa');
      }
      return;
    }

    if (telegramUser) {
      navigate('/sentry-pwa/wallet');
    } else {
      navigate('/sentry-pwa');
    }
  };

  const handleLogout = async () => {
    try {
      if (mode === 'agent') {
        await logoutAgentAccount();
        return;
      }
      if (telegramUser && 'authType' in telegramUser && telegramUser.authType === 'account') {
        await logoutPwaAccount();
      } else {
        await logoutTelegram();
      }
    } finally {
      setAgentUser(null);
      setTelegramUser(null);
      navigate('/sentry-pwa');
    }
  };

  return (
    <HeaderContainer>
      <LogoSection onClick={() => navigate('/sentry-pwa')}>
        <Logo src="/Sentry-Logo.svg" alt="Sentry" />
      </LogoSection>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        {((mode === 'agent' && agentUser) || (mode === 'pwa' && telegramUser)) && (
          <LogoutButton type="button" onClick={handleLogout} title="Sign out">
            Logout
          </LogoutButton>
        )}
        <StatusButton
          $isConnected={mode === 'agent' ? Boolean(agentUser) : Boolean(telegramUser)}
          onClick={handleStatusClick}
          title={mode === 'agent'
            ? (agentUser ? 'View agent wallet' : 'Sign in')
            : (telegramUser ? 'View wallet' : 'Sign in')}
        >
          <StatusIcon className={(mode === 'agent' ? agentUser : telegramUser) ? 'fas fa-check-circle' : 'fas fa-user-circle'} />
          <StatusText>
            {mode === 'agent'
              ? (agentUser ? `@${agentUser.agentName}` : 'Sign In')
              : (telegramUser?.username ? `@${telegramUser.username}` : telegramUser ? 'Connected' : 'Sign In')}
          </StatusText>
        </StatusButton>
      </div>
    </HeaderContainer>
  );
};

export default SentryPWAHeader;
